import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiRequest from "../ApiRequest";
import { requestURLs } from "@/Environment";
import { QUERY_KEYS } from "../query/querykeys";

export interface CreateDeliverablePayload {
  name: string;
  destinationId: string;
  typeId?: string | null;
  reference?: string | null;
  ownerId: string;
  status?: string;
  deliveryDueDate: Date | string;
  approvalRequired: boolean;
  approverId?: string | null;
  approvalDueDate?: Date | string | null;
  description?: string | null;
  acceptanceCriteria?: string | null;
  contributorIds?: string[];
  linkedTaskIds?: string[];
  inputDocuments?: Array<{
    title: string;
    documentType: "FILE" | "LINK";
    fileUrl?: string | null;
    fileSize?: number | null;
    mimeType?: string | null;
    linkUrl?: string | null;
  }>;
  initialVersion?: {
    versionNumber: string;
    versionNote?: string | null;
    documentType?: "FILE" | "LINK" | "NONE";
    title?: string | null;
    fileUrl?: string | null;
    fileSize?: number | null;
    linkUrl?: string | null;
  } | null;
  duplicatedFromDeliverableId?: string | null;
}

export function useCreateDeliverableMutation(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateDeliverablePayload) => {
      const res = await ApiRequest.post(`${requestURLs.deliverables}/${projectId}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableKpis, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectDetails, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjects] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectsNocal] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ProjectMember, projectId] });
    },
  });
}

export function useUpdateDeliverableMutation(deliverableId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<CreateDeliverablePayload>) => {
      const res = await ApiRequest.put(`${requestURLs.deliverables}/detail/${deliverableId}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableKpis, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectDetails, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjects] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectsNocal] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ProjectMember, projectId] });
    },
  });
}

export function useDeleteDeliverableMutation(deliverableId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await ApiRequest.delete(`${requestURLs.deliverables}/detail/${deliverableId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableKpis, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectDetails, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjects] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectsNocal] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ProjectMember, projectId] });
    },
  });
}

export function useRecordDeliveryMutation(deliverableId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      versionId?: string | null;
      newVersion?: {
        versionNumber: string;
        versionNote?: string | null;
        documentType?: "NONE" | "FILE" | "LINK";
        title?: string | null;
        fileUrl?: string | null;
        fileSize?: number | null;
        linkUrl?: string | null;
      } | null;
      deliveryDate: Date | string;
      comment?: string | null;
    }) => {
      const res = await ApiRequest.post(
        `${requestURLs.deliverables}/detail/${deliverableId}/delivery`,
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableKpis, projectId] });
    },
  });
}

export function useRecordApprovalMutation(deliverableId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      versionId: string;
      approvedByUserId: string;
      approvalDate: Date | string;
      comment?: string | null;
    }) => {
      const res = await ApiRequest.post(
        `${requestURLs.deliverables}/detail/${deliverableId}/approval`,
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableKpis, projectId] });
    },
  });
}

export function useRequestChangesMutation(deliverableId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      versionId: string;
      requestedByUserId: string;
      requestDate: Date | string;
      requestedChanges: string;
    }) => {
      const res = await ApiRequest.post(
        `${requestURLs.deliverables}/detail/${deliverableId}/change-request`,
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableKpis, projectId] });
    },
  });
}

export function useCancelDeliverableMutation(deliverableId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { reason: string }) => {
      const res = await ApiRequest.post(
        `${requestURLs.deliverables}/detail/${deliverableId}/cancel`,
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableKpis, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectDetails, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjects] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectsNocal] });
    },
  });
}

// Associations & List Value mutations
export function useManageListValuesMutation(projectId: string) {
  const queryClient = useQueryClient();
  return {
    createValue: useMutation({
      mutationFn: async (payload: { listType: "DESTINATION" | "TYPE"; value: string }) => {
        const res = await ApiRequest.post(`${requestURLs.deliverables}/${projectId}/list-values`, payload);
        return res.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableListValues, projectId] });
      },
    }),
    updateValue: useMutation({
      mutationFn: async ({ listValueId, value }: { listValueId: string; value: string }) => {
        const res = await ApiRequest.put(
          `${requestURLs.deliverables}/${projectId}/list-values/${listValueId}`,
          { value }
        );
        return res.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableListValues, projectId] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
      },
    }),
    deleteValue: useMutation({
      mutationFn: async (listValueId: string) => {
        const res = await ApiRequest.delete(
          `${requestURLs.deliverables}/${projectId}/list-values/${listValueId}`
        );
        return res.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableListValues, projectId] });
      },
    }),
  };
}

export function useLinkTasksMutation(deliverableId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (taskIds: string[]) => {
      const res = await ApiRequest.post(
        `${requestURLs.deliverables}/detail/${deliverableId}/tasks`,
        { taskIds }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
    },
  });
}

export function useUnlinkTaskMutation(deliverableId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      const res = await ApiRequest.delete(
        `${requestURLs.deliverables}/detail/${deliverableId}/tasks/${taskId}`
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
    },
  });
}

export function useAddDeliverableCommentMutation(deliverableId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (comment: string) => {
      const res = await ApiRequest.post(
        `${requestURLs.deliverables}/detail/${deliverableId}/comments`,
        { comment }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
    },
  });
}

export function useAddInputDocumentMutation(deliverableId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      title: string;
      documentType: "FILE" | "LINK";
      fileUrl?: string | null;
      fileSize?: number | null;
      mimeType?: string | null;
      linkUrl?: string | null;
    }) => {
      const res = await ApiRequest.post(
        `${requestURLs.deliverables}/detail/${deliverableId}/input-documents`,
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
    },
  });
}

export function useDeleteInputDocumentMutation(deliverableId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string) => {
      const res = await ApiRequest.delete(
        `${requestURLs.deliverables}/detail/${deliverableId}/input-documents/${documentId}`
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
    },
  });
}

export function useAddDeliverableVersionMutation(deliverableId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      versionNumber: string;
      versionNote?: string | null;
      documentType?: "NONE" | "FILE" | "LINK";
      title?: string | null;
      fileUrl?: string | null;
      fileSize?: number | null;
      linkUrl?: string | null;
    }) => {
      const res = await ApiRequest.post(
        `${requestURLs.deliverables}/detail/${deliverableId}/versions`,
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverableDetail, deliverableId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
    },
  });
}
